import { useState, useEffect, useCallback, useRef } from 'react';
import { styled, keyframes } from 'styled-components';
import { addSongToPlaylist, type YouTubeSearchResult } from '../../lib/playlistService';

type PlaylistSearchModalProps = {
    open: boolean;
    onClose: () => void;
    playlistApiUrl: string;
    youtubeApiKey: string;
    onSongAdded?: () => void;
};

const DEBOUNCE_DELAY = 300; // ms

export function PlaylistSearchModal({
    open,
    onClose,
    playlistApiUrl,
    youtubeApiKey,
    onSongAdded,
}: PlaylistSearchModalProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<YouTubeSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isAdding, setIsAdding] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Create AbortController when modal opens, clean up on close
    useEffect(() => {
        if (open) {
            abortControllerRef.current = new AbortController();
        } else {
            // Abort any pending requests when modal closes
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            setSearchQuery('');
            setSearchResults([]);
            setError(null);
        }

        return () => {
            // Cleanup on unmount
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [open]);

    // Debounced search
    useEffect(() => {
        if (!open) {
            return;
        }

        if (!searchQuery.trim()) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        const timer = setTimeout(() => {
            performSearch(searchQuery.trim());
        }, DEBOUNCE_DELAY);

        return () => {
            clearTimeout(timer);
        };
    }, [searchQuery, open]);

    const performSearch = async (query: string) => {
        if (!query || !youtubeApiKey) {
            setIsSearching(false);
            return;
        }

        try {
            setError(null);
            const searchUrl = `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query)}&type=video&maxResults=10&key=${youtubeApiKey}`;
            
            const response = await fetch(searchUrl);
            
            if (!response.ok) {
                throw new Error(`YouTube API error: ${response.statusText}`);
            }

            const data = await response.json();
            
            const results: YouTubeSearchResult[] = data.items.map((item: any) => ({
                videoId: item.id.videoId,
                title: item.snippet.title,
                artist: item.snippet.channelTitle,
                thumbnailUrl: item.snippet.thumbnails.medium?.url || item.snippet.thumbnails.default?.url || '',
                youtubeUrl: `https://www.youtube.com/watch?v=${item.id.videoId}`,
            }));

            setSearchResults(results);
        } catch (err) {
            console.error('Search error:', err);
            setError(err instanceof Error ? err.message : 'Failed to search YouTube');
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddSong = useCallback(async (song: YouTubeSearchResult) => {
        if (isAdding) return;

        setIsAdding(song.videoId);
        setError(null);

        try {
            const signal = abortControllerRef.current?.signal;
            await addSongToPlaylist(playlistApiUrl, song, signal);
            // Only call onSongAdded if request wasn't aborted
            if (!signal?.aborted) {
                onSongAdded?.();
            }
            // Optionally close modal or show success message
        } catch (err) {
            // Don't show error if request was aborted
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }
            console.error('Add song error:', err);
            setError(err instanceof Error ? err.message : 'Failed to add song to playlist');
        } finally {
            setIsAdding(null);
        }
    }, [playlistApiUrl, onSongAdded, isAdding]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!open) return null;

    return (
        <Backdrop onClick={handleBackdropClick} data-open={open}>
            <ModalContent onClick={(e) => e.stopPropagation()} data-open={open}>
                <Header>
                    <Title>Add Songs to Playlist</Title>
                    <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
                </Header>

                <SearchContainer>
                    <SearchInput
                        type="text"
                        placeholder="Search YouTube for songs..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                    {isSearching && <SearchSpinner />}
                </SearchContainer>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <ResultsContainer>
                    {searchResults.length === 0 && !isSearching && searchQuery.trim() && (
                        <EmptyState>No results found</EmptyState>
                    )}
                    {searchResults.length === 0 && !isSearching && !searchQuery.trim() && (
                        <EmptyState>Start typing to search for songs...</EmptyState>
                    )}
                    {searchResults.map((song) => (
                        <ResultItem key={song.videoId}>
                            <Thumbnail src={song.thumbnailUrl} alt={song.title} />
                            <SongInfo>
                                <SongTitle>{song.title}</SongTitle>
                                <SongArtist>{song.artist}</SongArtist>
                            </SongInfo>
                            <AddButton
                                onClick={() => handleAddSong(song)}
                                disabled={isAdding === song.videoId}
                                data-adding={isAdding === song.videoId}
                            >
                                {isAdding === song.videoId ? 'Adding...' : 'Add'}
                            </AddButton>
                        </ResultItem>
                    ))}
                </ResultsContainer>
            </ModalContent>
        </Backdrop>
    );
}

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    opacity: 0;
    pointer-events: none;
    transition: opacity 200ms ease-out;

    &[data-open='true'] {
        opacity: 1;
        pointer-events: auto;
    }
`;

const ModalContent = styled.div`
    background: rgba(20, 20, 20, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.1);
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 300ms ease, transform 300ms ease;

    &[data-open='true'] {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #fff;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: #fff;
    font-size: 32px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 150ms;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const SearchContainer = styled.div`
    padding: 20px 24px;
    position: relative;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 16px;
    outline: none;
    transition: border-color 150ms, background-color 150ms;

    &:focus {
        border-color: rgba(255, 255, 255, 0.4);
        background: rgba(255, 255, 255, 0.15);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }
`;

const SearchSpinner = styled.div`
    position: absolute;
    right: 36px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        to {
            transform: translateY(-50%) rotate(360deg);
        }
    }
`;

const ErrorMessage = styled.div`
    padding: 12px 24px;
    background: rgba(220, 53, 69, 0.2);
    color: #ff6b6b;
    border-left: 3px solid #ff6b6b;
    margin: 0 24px;
    border-radius: 4px;
    font-size: 14px;
`;

const ResultsContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ResultItem = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: background-color 150ms, border-color 150ms;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.2);
    }
`;

const Thumbnail = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 4px;
    object-fit: cover;
    flex-shrink: 0;
`;

const SongInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SongTitle = styled.div`
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SongArtist = styled.div`
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AddButton = styled.button`
    padding: 8px 16px;
    border-radius: 6px;
    border: none;
    background: #fff;
    color: #000;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 150ms, transform 150ms;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &[data-adding='true'] {
        opacity: 0.7;
    }
`;

const EmptyState = styled.div`
    padding: 40px 20px;
    text-align: center;
    color: rgba(255, 255, 255, 0.5);
    font-size: 16px;
`;

